import React from "react";
import styled from "styled-components";
import { theme } from "../theme";
import { Network } from "../types";

const Container = styled.div<{ $isOpen?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: ${(props) => (props.$isOpen ? 100 : 1)};
`;

const Option = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: ${(props) =>
    props.$selected ? theme.colors.blue + "20" : "transparent"};
  border: 1px solid
    ${(props) => (props.$selected ? theme.colors.blue : "transparent")};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: translateY(0);

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0.75rem;
    gap: 0.5rem;
    border-radius: 9px;
  }

  &:hover {
    border-color: ${theme.colors.blue};
    background: ${(props) =>
      props.$selected ? theme.colors.blue + "30" : theme.colors.border + "20"};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(58, 134, 255, 0.2);
  }
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 24px;
    height: 24px;
    padding: 3px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Name = styled.span`
  font-weight: 600;
  color: ${theme.colors.text};
  font-size: 1rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 0.85rem;
  }
`;

const Description = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.textSecondary};

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 0.7rem;
  }
`;

const Arrow = styled.span<{ $open: boolean }>`
  font-size: 0.8rem;
  color: ${theme.colors.textSecondary};
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0deg)")};
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999;
  margin-top: 4px;
  background: ${theme.colors.darker};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4), 0 0 60px rgba(58, 134, 255, 0.1);
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
  pointer-events: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-bottom: 1px solid ${theme.colors.border};
  padding: 0.75rem 1rem;
  color: ${theme.colors.text};
  font-size: 0.9rem;
  outline: none;

  &::placeholder {
    color: ${theme.colors.textSecondary};
  }

  &:focus {
    background: rgba(0, 0, 0, 0.6);
    border-bottom-color: ${theme.colors.blue};
  }
`;

const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
`;

interface NetworkSelectorProps {
  label?: string;
  selectedId: string;
  networks: Network[];
  onSelect: (id: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  search: string;
  onSearchChange: (value: string) => void;
}

export const NetworkSelector: React.FC<NetworkSelectorProps> = ({
  label,
  selectedId,
  networks,
  onSelect,
  isOpen,
  onToggle,
  search,
  onSearchChange,
}) => {
  const selectedNetwork = networks.find((n) => n.id === selectedId);

  const filteredNetworks = networks
    .filter((n) => n.id !== selectedId)
    .filter(
      (n) =>
        n.name.toLowerCase().includes(search.toLowerCase()) ||
        (n.description &&
          n.description.toLowerCase().includes(search.toLowerCase()))
    );

  return (
    <InputGroup>
      {label && <Label>{label}</Label>}
      <Container $isOpen={isOpen}>
        <Option
          $selected={true}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {selectedNetwork ? (
            <>
              <Logo src={selectedNetwork.logo} alt={selectedNetwork.name} />
              <Info>
                <Name>{selectedNetwork.name}</Name>
                {/* <Description>{selectedNetwork.description}</Description> */}
              </Info>
              <Arrow $open={isOpen}>▼</Arrow>
            </>
          ) : (
            <>
              <Info>
                <Name>Select Network</Name>
              </Info>
              <Arrow $open={isOpen}>▼</Arrow>
            </>
          )}
        </Option>

        {isOpen && (
          <Dropdown onClick={(e) => e.stopPropagation()}>
            <SearchInput
              type="text"
              placeholder="Search networks..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />

            {filteredNetworks.length > 0 ? (
              filteredNetworks.map((network) => (
                <Option
                  key={network.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(network.id);
                    onToggle();
                    onSearchChange("");
                  }}
                >
                  <Logo src={network.logo} alt={network.name} />
                  <Info>
                    <Name>{network.name}</Name>
                    <Description>{network.description}</Description>
                  </Info>
                </Option>
              ))
            ) : (
              <NoResults>No networks found</NoResults>
            )}
          </Dropdown>
        )}
      </Container>
    </InputGroup>
  );
};
